import {
  ITestPlanSchema,
  testPlanSchema,
  TestPlanService,
} from '@/api/testPlan';
import { InputField } from '@/components/form/fields/InputField';
import { TextAreaField } from '@/components/form/fields/TextAreaField';
import { FormContainer, FormFooter } from '@/components/form/FormContainer';
import { Loading } from '@/components/Loading';
import { Section } from '@/components/Section';
import { OutlineButton } from '@/components/shared/button/OutlineButton';
import { useBackNavigate } from '@/utils/hooks/useBackNavigate';
import { zodResolver } from '@hookform/resolvers/zod';
import { Button, Dialog } from 'alurkerja-ui';
import React from 'react';
import { useForm } from 'react-hook-form';
import { useParams } from 'react-router-dom';

const formatDate = (dateString?: string) =>
  dateString ? new Date(dateString).toISOString().split('T')[0] : undefined;

export const CreateEditTestPlan: React.FC = () => {
  const { id, testPlanId } = useParams();
  const backNavigate = useBackNavigate();

  const {
    data,
    isFetching: isFetchLoading,
    refetch,
  } = TestPlanService.useGet(testPlanId);
  const { mutate: create, isPending: isCreateLoading } =
    TestPlanService.useCreate();
  const { mutate: update, isPending: isUpdateLoading } =
    TestPlanService.useUpdate();
  const isLoading = isCreateLoading || isUpdateLoading;

  const form = useForm<ITestPlanSchema>({
    resolver: zodResolver(testPlanSchema),
    values: {
      ...data,
      plan_name: data?.plan_name || '',
      expiration_time: formatDate(data?.expiration_time),
      assignment_time: formatDate(data?.assignment_time),
    },
  });

  const submitAction = (data: ITestPlanSchema) => {
    data.project_id = id;
    delete data.code;

    if (testPlanId) {
      update(data, {
        onSuccess: () => {
          Dialog.success({
            title: 'Berhasil',
            description: 'Berhasil update Test Plan',
            callback: () => {
              refetch();
              backNavigate();
            },
          });
        },
      });
    } else {
      create(data, {
        onSuccess: () => {
          Dialog.success({
            title: 'Berhasil',
            description: 'Berhasil membuat Test Plan',
            callback: () => {
              refetch();
              backNavigate();
            },
          });
        },
      });
    }
  };

  return (
    <Section title={testPlanId ? 'Update Test Plan' : 'Add Test Plan'}>
      {isFetchLoading ? (
        <Loading />
      ) : (
        <FormContainer form={form} onSubmit={submitAction}>
          <div className="flex flex-row gap-10">
            <div className="flex flex-col w-full gap-3">
              <InputField
                name="plan_name"
                label="Test Plan Name"
                hasRequiredAsterisk
                placeholder="Enter Test Plan Name"
              />

              <TextAreaField name="description" label="Description" />
            </div>

            <div className="flex flex-col w-full gap-3 ">
              <InputField
                name="assignment_time"
                label="Assignment Time"
                type="date"
              />

              <InputField
                name="expiration_time"
                label="Expiration Time"
                type="date"
              />
            </div>
          </div>

          <FormFooter>
            <OutlineButton onClick={backNavigate}>Back</OutlineButton>

            <Button loading={isLoading}>
              {testPlanId ? 'Update' : 'Create'}
            </Button>
          </FormFooter>
        </FormContainer>
      )}
    </Section>
  );
};
